import { Key, useEffect, useState } from "react";
import { Stepper, Icon, Icons, StepItemProps } from "@ir/ui";
import { Deduction } from "@/core/entities/deduction";
import { useLookup } from "@/hooks";

const stageIcons = {
  DOCUMENTATION: Icons.File,
  ANALYSIS: Icons.Search,
  RESOLUTION: Icons.Flash,
  CLOSED: Icons.Check,
};

const getPreSteps = (steps: StepItemProps[], isLoading: boolean) =>
  steps.map((step: StepItemProps, index: number) => {
    return {
      key: `loading-${index + 1}`,
      label: step.label.charAt(0).toUpperCase() + step.label.toLowerCase().slice(1),
      icon: <Icon name={isLoading ? Icons.Loading : stageIcons[step.icon as keyof typeof stageIcons]} className={`${isLoading ? "icon-fast-loading" : ""}`} />,
      isDisabled: true,
      isReadOnly: true,
    };
  });

const DeductionStep = ({ deduction, isLoading }: { deduction: Deduction; isLoading: boolean }) => {
  // States and Hooks
  const [activeKey, setActiveKey] = useState<Key | undefined>(undefined);
  const { stagesLookup, getStagesLookup } = useLookup();
  const [defaultSelectedKeys, setDefaultSelectedKeys] = useState<Key[]>([]);

  // Data for stages
  const { data: stages } = stagesLookup;

  const deductionSteps: StepItemProps[] = stages
    .filter(stage => stage.parentId == null)
    ?.map(stage => ({
      key: stage.id,
      label: stage.stageName,
      icon: <Icon name={stageIcons[stage.stageKey as keyof typeof stageIcons]} />,
      isDisabled: isLoading,
      isReadOnly: true,
    }));

  useEffect(() => {
    if (!deduction) {
      return;
    }
    const currentStage = deduction?.stages.find(stage => stage.isCurrent);
    if (currentStage) {
      setTimeout(() => {
        setActiveKey(currentStage.stageId);
      }, 0);
    }
  }, [deduction]);

  useEffect(() => {
    getStagesLookup();
    return () => {
      setDefaultSelectedKeys([]);
    };
  }, [getStagesLookup]);

  useEffect(() => {
    const setSelectedKeys = () => {
      if (!deduction) {
        setDefaultSelectedKeys([]);
      } else if (deduction?.id) {
        setDefaultSelectedKeys(deduction?.stages.map(stage => stage.stageId));
      } else {
        setDefaultSelectedKeys([]);
      }
      setActiveKey(deduction?.stages.find(stage => stage.isCurrent)?.stageId ?? undefined);
    };

    setSelectedKeys();
  }, [deduction]);

  return (
    <Stepper
      steps={isLoading ? getPreSteps(deductionSteps, isLoading) : deductionSteps}
      selectedKeys={defaultSelectedKeys}
      activeKey={activeKey}
      size="small"
      className="custom-stepper p-0 me-5"
      aria-label="Deduction Stepper"
    />
  );
};

export default DeductionStep;
